import { QueryKey, useInfiniteQuery, UseInfiniteQueryOptions, useMutation, useQuery } from 'react-query';
import { auditionScheduleDao } from 'shared/dao/auditionScheduleDao';
import {
  IAuditionRequestPayload,
  IAuditionRequestRespondPayload,
  IAuditionRequestResponsePayload,
  IAuditionScheduleCreatePayload,
  IAuditionScheduleResponsePayload,
  IAuditionSchedulesRequestPayload,
  IAuditionSchedulesResponsePayload,
  ISelfTapesScheduleResponse,
  ISpecificAuditionResponsePayload,
  IUpdateAuditionScheduleRequestPayload,
} from 'shared/interfaces/IAuditionSchedule';
import { IAxiosErrorResponse } from 'shared/interfaces/utils/IAxios';

const {
  createAuditionSchedule: createAuditionScheduleDao,
  createAuditionScheduleSelfTapes: createAuditionScheduleSelfTapesDao,
  markAsDoneSelfTapes: markAsDoneSelfTapesDao,
  getAuditionSchedules: getAuditionSchedulesDao,
  getSpecificAuditionSchedule: getSpecificAuditionScheduleDao,
  getAuditionRequest: getAuditionRequestDao,
  updateAuditionRequest: updateAuditionRequestDao,
  updateAuditionSchedule: updateAuditionScheduleDao,
} = auditionScheduleDao();
export const auditionScheduleService = () => {
  const createAuditionSchedule = () => {
    return useMutation<
      IAuditionScheduleResponsePayload,
      IAxiosErrorResponse,
      { sessionId: string; payload: IAuditionScheduleCreatePayload }
    >(({ sessionId, payload }) => createAuditionScheduleDao(sessionId, payload));
  };

  const createAuditionScheduleSelfTapes = () => {
    return useMutation<ISelfTapesScheduleResponse, IAxiosErrorResponse, { payload: IAuditionScheduleCreatePayload }>(
      ({ payload }) => createAuditionScheduleSelfTapesDao(payload),
    );
  };

  const markAsDoneSelfTapes = () => {
    return useMutation<
      ISelfTapesScheduleResponse,
      IAxiosErrorResponse,
      { auditionId: string; payload: IAuditionScheduleCreatePayload }
    >(({ auditionId, payload }) => markAsDoneSelfTapesDao(auditionId, payload));
  };

  const updateAuditionRequest = () => {
    return useMutation<
      ISelfTapesScheduleResponse,
      IAxiosErrorResponse,
      { auditionId: string; payload: IAuditionRequestRespondPayload }
    >(({ auditionId, payload }) => updateAuditionRequestDao(auditionId, payload));
  };

  const getAuditionSchedules = (
    payload: IAuditionSchedulesRequestPayload,
    options?: Omit<
      UseInfiniteQueryOptions<
        IAuditionSchedulesResponsePayload,
        IAxiosErrorResponse,
        IAuditionSchedulesResponsePayload,
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useInfiniteQuery<IAuditionSchedulesResponsePayload, IAxiosErrorResponse, IAuditionSchedulesResponsePayload>(
      ['auditionSchedule', payload],
      ({ pageParam = '' }) => getAuditionSchedulesDao({ ...payload, after: pageParam }),
      {
        getNextPageParam: (lastPage) => {
          return lastPage.data.talent_audition_schedules_connection.pageInfo.hasNextPage
            ? lastPage.data.talent_audition_schedules_connection.pageInfo.endCursor
            : undefined;
        },
        enabled: options?.enabled,
      },
    );
  };

  const getAuditionRequest = (
    payload: IAuditionRequestPayload,
    options?: Omit<
      UseInfiniteQueryOptions<
        IAuditionRequestResponsePayload,
        IAxiosErrorResponse,
        IAuditionRequestResponsePayload,
        QueryKey
      >,
      'queryKey' | 'queryFn'
    >,
  ) => {
    return useInfiniteQuery<IAuditionRequestResponsePayload, IAxiosErrorResponse, IAuditionRequestResponsePayload>(
      ['auditionRequest', payload],
      ({ pageParam = '' }) => getAuditionRequestDao({ ...payload, after: pageParam }),
      {
        getNextPageParam: (lastPage) => {
          return lastPage.data.audition_schedule_requests_connection.pageInfo.hasNextPage
            ? lastPage.data.audition_schedule_requests_connection.pageInfo.endCursor
            : undefined;
        },
        enabled: options?.enabled,
      },
    );
  };

  const getSpecificAuditionSchedule = (id: string) => {
    return useQuery<ISpecificAuditionResponsePayload, IAxiosErrorResponse>(['auditionSchedule', id], () =>
      getSpecificAuditionScheduleDao(id),
    );
  };

  const updateAuditionSchedule = () => {
    return useMutation<IAuditionScheduleResponsePayload, IAxiosErrorResponse, IUpdateAuditionScheduleRequestPayload>(
      (payload) => updateAuditionScheduleDao(payload),
    );
  };

  return {
    updateAuditionSchedule,
    markAsDoneSelfTapes,
    createAuditionSchedule,
    createAuditionScheduleSelfTapes,
    getAuditionSchedules,
    getSpecificAuditionSchedule,
    getAuditionRequest,
    updateAuditionRequest,
  };
};
